import { Component, output, OnInit, ElementRef } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { GuidId, Person } from '@app/core/stingray-api/models';
import { PersonsService } from '@app/core/stingray-api/services';
import moment from 'moment';
import { finalize } from 'rxjs/operators';
import { GoogleAnalyticsService } from '@app/core/analytics/google-analytics.service';
import { StingrayAuthenticationService } from '@app/core/authentication/stingray-authentication.service';
import { createAddressFormGroup } from '@app/shared/address-input-v2/address-input-v2-tools';
import { LoadingButtonComponent } from '../../loading-button/loading-button.component';

import { CustomerSettingsComponent } from '../../customer-settings/customer-settings.component';
import { AddressInputV2Component } from '../../address-input-v2/address-input-v2.component';
import { NationalityAutocompleteComponent } from '../../nationality-autocomplete/nationality-autocomplete.component';
import { TaxPartnerAutocompleteComponent } from '../../tax-partner-autocomplete/tax-partner-autocomplete.component';
import { IncomeSourceFieldComponent } from '../../forms/income-source-field/income-source-field.component';
import { MaritalStatusInputComponent } from '../../maritalstatus-input/maritalstatus-input.component';
import { MatDatepickerInput, MatDatepickerToggle, MatDatepicker } from '@angular/material/datepicker';
import { MatInput } from '@angular/material/input';
import { MatFormField, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatRadioGroup, MatRadioButton } from '@angular/material/radio';
import { TranslateModule } from '@ngx-translate/core';
import { MatIcon } from '@angular/material/icon';

@Component({
    selector: 'unite-create-person',
    templateUrl: './create-person.component.html',
    styleUrls: ['./create-person.component.scss'],
    imports: [ReactiveFormsModule, MatIcon, TranslateModule, MatRadioGroup, MatRadioButton, MatFormField, MatLabel, MatInput, MatDatepickerInput, MatDatepickerToggle, MatSuffix, MatDatepicker, MaritalStatusInputComponent, IncomeSourceFieldComponent, TaxPartnerAutocompleteComponent, NationalityAutocompleteComponent, AddressInputV2Component, CustomerSettingsComponent, LoadingButtonComponent]
})
export class CreatePersonComponent implements OnInit {

  person = output<Person>();
  personGroup: UntypedFormGroup;

  isLoadingCreate = false;
  supportsConsumerRates = false;

  constructor(
    private el: ElementRef,
    private googleAnalyticsService: GoogleAnalyticsService,
    private stingrayAuthenticationService: StingrayAuthenticationService,
    private formBuilder: UntypedFormBuilder,
    private personsService: PersonsService) {
    this.personGroup = this.formBuilder.group({
      gender: ['Male', [Validators.required]],
      initials: [, [Validators.required]],
      firstName: [, [Validators.required]],
      givenNames: [],
      middleName: [],
      surname: [, [Validators.required]],
      birthdate: [],
      phone: [],
      mobile: [],
      email: [],
      iban: [],
      businessunit: [],
      address: createAddressFormGroup(this.formBuilder),
      nationality: [],
      maritalStatus: [],
      taxPartner: [],
      adviser: [],
      isConsumer: [],
      existing: [false],
      incomeSources: [],
    });
  }

  ngOnInit(): void {
    this.supportsConsumerRates = this.stingrayAuthenticationService.organization.supportsConsumerRates;

    if (!this.stingrayAuthenticationService.organization.supportsConsumerRates) {
      this.personGroup.get('isConsumer').setValue(false);
      this.personGroup.get('isConsumer').disable();
    }
  }

  create(): void {
    this.personGroup.markAllAsTouched();

    if (this.personGroup.valid) {
      this.googleAnalyticsService.trackEvent('customer', 'add', 'person');
      this.isLoadingCreate = true;
      const person = <Person>this.personGroup.value;
      if (person.birthdate) {
        person.birthdate = (<moment.Moment>this.personGroup.value.birthdate).locale('en').format('L');
      }

      this.personsService.addPerson({ body: person }).pipe(finalize(() => {
        this.isLoadingCreate = false;
      })).subscribe((response: GuidId) => {
        person.id = response.id;
        person.name = `${this.personGroup.get('initials').value} ${this.personGroup.get('surname').value}`;
        this.person.emit(person);
      });
    } else {
      this.scrollToFirstInvalidControl();
    }
  }

  private scrollToFirstInvalidControl(): void {
    const firstInvalidControl: HTMLElement = this.el.nativeElement.querySelector('form .ng-invalid');
    firstInvalidControl.scrollIntoView();
  }
}
